import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuthToken, getcompanyId, getFacilityId, getuserId } from './utility/tokenUtility';
import axios from 'axios';
import { ifacSecurityURL } from './utility/apiURLS';
import { useSelector } from 'react-redux';
import { Inventory } from '@mui/icons-material';

// Create the Context
const AccessContext = createContext();

// Custom hook to use the Access Context
export const useAccess = () => {
    return useContext(AccessContext);
};

// Mock module to screen mapping
const moduleMapping = {
    hospitalMasters: [
        "ENGM001",
        "ENGM003",
        "ENGM004",
        "ENGM018",
        "ENGM005",
        "ENGM006",
        "ENGM007",
        "ENGM008",
        "ENGM009",
        "ENGM010",
        "ENGM011",
        "ENGC002"
    ],
    maintenance: [
        "ENGT010",
        "ENGT006",
        "ENGT001",
        "ENGT002",
        "ENGT003",
        "ENGT004",
        "RIPT001",
        "SCMT001",
        "PRMT001"
    ],
    assets: [
        "TNCG001",
        "ENGM014",
        "ENGM013",
        "ENGM013A",
        "ENGM013C",
        "ENGT007",
        "ENGM015",
        "ENGM016"
    ],
    staff: ["ENGM002", "SDRT001"],
    inventory: ["IMSM005", "IMSM001", "STRG001"],
    vendor: ["POMM002"],
    contract: ["ENGT005"],
    report: {
        workOrder: ["ENGA001"],
        asset: ["ENGA002", "ENGA003", "ENGA004","ENGA005"],
        staff: ["SDRT002", "SDRT003"],
        others: [
            "ENGA009A",
            "ENGA009B",
            "ENGA009C",
            "ENGA008",
            "ENGA009",
            "STRA001"
        ]
    },
    calendar: ["ENGA006"],
    options: ["ENGO001", "ENGO002","ENGUS001"],
    powerBIdashboard: ["ENG1001"],
};

// Utility function to check if access is all zeros (no access)
const hasNoAccess = (access) => access === "000000000000";

// Provider component
export const AccessProvider = ({ children }) => {

    const [accessData, setAccessData] = useState([]);
    const [moduleAccess, setModuleAccess] = useState({});
    const [screenAccess, setScreenAccess] = useState({});  // To track individual screen access

    const updateAccessData = (newData) => {
          // Otherwise, update state and sync with localStorage
          setAccessData(newData);
          processAccessData(newData);
          localStorage.setItem("accessData", JSON.stringify(newData));
    }

    useEffect(() => {
        const storedData = JSON.parse(localStorage.getItem("accessData"));
        if (storedData) {
            setAccessData(storedData);
            processAccessData(storedData);
        }
    }, []);


    // Process access data for modules and screens
    const processAccessData = (data) => {
        const moduleAccessResult = determineModuleAccess(data, moduleMapping);
        const screenAccessResult = determineScreenAccess(data);

        setModuleAccess(moduleAccessResult);
        setScreenAccess(screenAccessResult);
    };
    // Function to determine module access (handles nested modules)
    // Function to determine module access (handles nested modules)
    const determineModuleAccess = (accessData, moduleMapping) => {
        const moduleAccess = {};

        // Recursive function to handle nested modules
        const checkModuleAccess = (screens) => {
            if (Array.isArray(screens)) {
                // Check if any screen in the module has access
                return screens.some(screenname => {
                    const screenData = accessData.find(screen => screen.transCode === screenname);
                    return screenData && !hasNoAccess(screenData.permission);
                });
            } else if (typeof screens === 'object') {
                // If it's an object, recursively check sub-modules
                return Object.values(screens).some(subModuleScreens => checkModuleAccess(subModuleScreens));
            }
            return false;
        };

        // Loop through the module mapping
        for (const module in moduleMapping) {
            const screens = moduleMapping[module];

            // Set module access using the recursive check
            moduleAccess[module] = checkModuleAccess(screens);
        }

        // Initialize the reports object in moduleAccess
        moduleAccess.reports = {};

        // Variable to track if any report module has access
        let anyReportAccess = false;

        // Check access for each sub-module in reports
        for (const reportModule in moduleMapping.report) {
            const hasAccess = checkModuleAccess(moduleMapping.report[reportModule]);
            moduleAccess.reports[reportModule] = hasAccess;

            // Update the anyReportAccess flag
            if (hasAccess) {
                anyReportAccess = true;
            }
        }

        // Set the reports access to false if all sub-modules are false
        if (!anyReportAccess) {
            moduleAccess.reports = false;
        }

        return moduleAccess;
    };



    // Function to determine screen-level access (handles nested modules)
    const determineScreenAccess = (accessData) => {
        const screenAccess = {};

        const checkScreenAccess = (screens) => {
            if (Array.isArray(screens)) {
                screens.forEach(screenname => {
                    
                    const screenData = accessData.find(screen => screen.transCode === screenname);
                    if (screenData) {
                        screenAccess[screenname] = !hasNoAccess(screenData.permission);
                    }
                });
            } else if (typeof screens === 'object') {
                Object.values(screens).forEach(subModuleScreens => checkScreenAccess(subModuleScreens));
            }
        };

        // Loop through the module mapping and check screen access
        Object.values(moduleMapping).forEach(screens => {
            checkScreenAccess(screens);
        });

        return screenAccess;
    };


    return (
        <AccessContext.Provider value={{ moduleAccess, screenAccess, accessData, updateAccessData }}>
            {children}
        </AccessContext.Provider>
    );
};
